import { useEffect, useState } from 'react';
import AcademicYearService from '../services/AcademicYearService';

/**
 * Hook to initialize academic year service cache early in the application lifecycle.
 * This ensures that week-to-calendar-year mapping is available for synchronous operations.
 * 
 * @returns {Object} - Status object with loading state and error information
 */
export const useAcademicYearInit = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeAcademicYear = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const academicYearService = AcademicYearService.getInstance();
        await academicYearService.getAcademicYearStructure();
        
        setIsInitialized(true);
        console.log('[useAcademicYearInit] Academic year service initialized successfully');
      } catch (err) {
        console.warn('[useAcademicYearInit] Failed to initialize academic year service:', err);
        setError(err.message || 'Failed to load academic year structure');
        // Don't block the app if academic year structure fails to load
        setIsInitialized(true); // Allow app to continue
      } finally {
        setIsLoading(false);
      }
    };

    initializeAcademicYear();
  }, []);

  // Provide a method to manually refresh the cache
  const refreshAcademicYear = async () => {
    try {
      setError(null);
      const academicYearService = AcademicYearService.getInstance();
      academicYearService.clearCache();
      await academicYearService.getAcademicYearStructure();
      console.log('[useAcademicYearInit] Academic year cache refreshed');
      return true;
    } catch (err) {
      console.error('[useAcademicYearInit] Failed to refresh academic year cache:', err);
      setError(err.message || 'Failed to refresh academic year structure');
      return false;
    }
  };

  return {
    isLoading,
    error,
    isInitialized,
    refreshAcademicYear
  };
};

export default useAcademicYearInit; 